import type { IProductEntity } from '@/entities/product/models/IProductEntity';
import { useProductLite } from '@/shared/composables/useApollo';
import { getSortOptionTitle } from '@/pages/search/-src/shared/helpers/business/sort-map';

export const createAnalyticsBuilder = (product: IProductEntity, context: any = {}) => ({
  product_id: () => Number(product.id),
  shop_id: async () => {
    const { fetchProductLite } = useProductLite();
    const { productLite } = await fetchProductLite(Number(product.id));
    return productLite?.shop?.id;
  },
  old_price: () => product.fullPrice,
  price: () => product.sellPrice,
  product_position: () => context.index,
  module_name: () => context.moduleName,
  module_id: () => context.moduleId,
  position: () => context.carouselPosition || context.position,
  module_size: () => context.moduleSize,
  product_name: () => product.title,
  rating_value: async () => {
    const { fetchProductLite } = useProductLite();
    const { productLite } = await fetchProductLite(Number(product.id));
    return productLite?.rating;
  },
  badges: () => {
    if (product.badges && product.badges.length > 0) {
      return product.badges.map((badge) => badge.text).join(';');
    }
  },
  discount_percent: () => {
    if (product.fullPrice && product.sellPrice && product.fullPrice > product.sellPrice) {
      const discount = Math.round(
        ((product.fullPrice - product.sellPrice) / product.fullPrice) * 100
      );
      return discount.toString();
    }
  },
  is_picture_list: async (): Promise<string | undefined> => {
    try {
      const { fetchProductLite } = useProductLite();
      const { productLite } = await fetchProductLite(Number(product.id));
      if (productLite?.photos && productLite.photos.length > 0) {
        const pictureStatuses = productLite.photos.map((photo) => {
          return photo.link?.high ? 'true' : 'false';
        });
        return JSON.stringify(pictureStatuses);
      }
    } catch {
      return JSON.stringify([]);
    }
  },
  search_query: () => context.searchQuery,
  search_text: () => context.searchQuery,
  sort_type: () => context.sortType,
  sort_type_name: () => {
    const sorting = context.sorting;
    const ordering = context.ordering;

    if (!sorting || !ordering) return 'Популярные';

    return getSortOptionTitle(sorting, ordering);
  },
  source: () => '',
  rev_num: () => product.feedbackQuantity,
  in_stock: () => product.availableAmount,
  wishlist_toggle: () => product.isFavorite,
  is_parsing_rating: () => product.externalRating,
  _img: () => product.photo?.key,
  cart_options: () => context.cartOptions,
  store_id: () => context.storeId,
  index: () => context.index,
  sku_id: () => product.skuId,
});

export const ANALYTICS_SELECTORS = {
  сarousel: [
    'product_position',
    'module_name',
    'module_id',
    'position',
    'module_size',
    'product_name',
    'rating_value',
    'badges',
    'discount_percent',
    'is_picture_list',
  ],
  productListing: ['is_picture_list', 'rating_value', 'in_stock', 'position', 'badges', 'rev_num'],
  itemScreen: [
    'sort_type_name',
    'in_stock',
    'is_picture_list',
    'wishlist_toggle',
    'rating_value',
    'rev_num',
    'badges',
  ],
  productScreen: [
    'rating_value',
    'source',
    'is_picture_list',
    'rev_num',
    'badges',
    'is_parsing_rating',
    'sku_id',
  ],
  searchScreen: [
    'search_text',
    'is_picture_list',
    '_img',
    'sort_type_name',
    'rating_value',
    'rev_num',
    'in_stock',
    'badges',
  ],
  shopScreen: [
    'sort_type_name',
    'in_stock',
    'is_picture_list',
    'wishlist_toggle',
    'rating_value',
    'rev_num',
    'badges',
  ],
  cartScreen: [
    'cart_options',
    'store_id',
    'product_name',
    'index'
  ],
};

export async function buildAnalyticsParams(
  selector: keyof typeof ANALYTICS_SELECTORS,
  product: IProductEntity,
  context: any
) {
  try {
    if (!product.id) return {};

    const selectedParams = ANALYTICS_SELECTORS[selector];
    if (!selectedParams) {
      console.warn(`Unknown analytics selector: ${selector}`);
      return {};
    }

    const result: Record<string, any> = {};
    const builder = createAnalyticsBuilder(product, context);
    const baseParams = ['product_id', 'shop_id', 'old_price', 'price'];
    const allParams = [...baseParams, ...selectedParams];

    for (const paramName of allParams) {
      const paramBuilder = builder[paramName as keyof typeof builder];
      if (paramBuilder) {
        try {
          result[paramName] = await paramBuilder();
        } catch (error) {
          console.warn(`Failed to build param ${paramName}:`, error);
          result[paramName] = undefined;
        }
      }
    }

    return result;
  } catch (error) {
    console.warn('Analytics params build error:', error);
    return {};
  }
}
